<script lang="ts" setup>
    import InputLabel from '../InputLabel.vue';
    import { __ } from '@/js/stores/expressions';
    import MarkdownTextarea from '../MarkdownTextarea.vue';
    import {
        CoachingFormAnswerType,
        ICoachingFormQuestion,
    } from '@/js/services/api/coachingForm/types';
    import EditCoachingFormAnswerAffirmative from './EditCoachingFormAnswerAffirmative.vue';
    import EditCoachingFormAnswerInterval from './EditCoachingFormAnswerInterval.vue';
    import EditCoachingFormAnswerCustom from './EditCoachingFormAnswerCustom.vue';
    import EditCoachingFormAnswerText from './EditCoachingFormAnswerText.vue';
    import { computed, watch } from 'vue';
    import ToggleSwitch from '../ToggleSwitch.vue';
    import IconButton from '../IconButton.vue';
    import { useEditCoachingFormStore } from '@/js/stores/edit-coaching-form';
    import CheckboxInput from '../CheckboxInput.vue';
    import { storeToRefs } from 'pinia';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import SelectInput from '../SelectInput.vue';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import { AstroidIcon, ChevronDownIcon, ChevronUpIcon, XIcon } from '@lucide/vue';

    const { removeQuestion, sortMoveUpQuestion, sortMoveDownQuestion, isLastQuestion } =
        useEditCoachingFormStore();
    const { sortForm } = storeToRefs(useEditCoachingFormStore());

    const emit = defineEmits<{
        (e: 'sorting'): void;
    }>();

    const { rememberFocus, restoreFocus } = useElementFocus();

    const question = defineModel<ICoachingFormQuestion>('question', { required: true });
    const props = defineProps<{
        sectionIndex: number;
        questionIndex: number;
        useAi: boolean;
        animating?: boolean;
    }>();

    // TODO, fix all templates
    const coachingFormAnswerComponents = {
        [CoachingFormAnswerType.Affirmative]: EditCoachingFormAnswerAffirmative,
        [CoachingFormAnswerType.Interval]: EditCoachingFormAnswerInterval,
        [CoachingFormAnswerType.Custom]: EditCoachingFormAnswerCustom,
        [CoachingFormAnswerType.Text]: EditCoachingFormAnswerText,
    };

    watch(
        () => question.value.answerType,
        (type) => {
            if (type === CoachingFormAnswerType.Text) {
                question.value.isCritical = false;
                question.value.useComments = false;
                question.value.notApplicable = false;
            }

            resetAnswers();
        }
    );

    const resetAnswers = () => {
        question.value.answers = [];
    };

    const questionPoints = computed(() => {
        return question.value.answers.length === 0
            ? 0
            : Math.max(...question.value.answers.map((answer) => answer.points));
    });

    const sortQuestion = (direction: 'up' | 'down') => {
        emit('sorting');

        if (direction === 'up') {
            sortMoveUpQuestion(props.sectionIndex, props.questionIndex);
        } else {
            sortMoveDownQuestion(props.sectionIndex, props.questionIndex);
        }
    };
</script>

<template>
    <div
        class="py-5 px-6 border-t border-dashed border-gray-light"
        :data-currently-animating="animating"
    >
        <div class="flex justify-between gap-4">
            <h3 class="heading-sm truncate" :title="question.title">
                {{
                    __('coaching-form-question-title', {
                        title: sectionIndex + 1 + '.' + (questionIndex + 1) + ' ' + question.title,
                        points: String(questionPoints),
                    })
                }}
            </h3>

            <div class="-mx-4 -my-3 flex flex-col gap-1">
                <template v-if="!sortForm">
                    <IconButton
                        class="text-error"
                        area-size="size-8"
                        size="size-5"
                        type="button"
                        :aria-label="__('remove-question', { question: question.title })"
                        @click="(emit('sorting'), removeQuestion(sectionIndex, questionIndex))"
                    >
                        <XIcon class="size-5" />
                    </IconButton>
                </template>

                <template v-else>
                    <IconButton
                        class="text-gray-dark bg-gray-lightest hover:bg-gray-light"
                        area-size="w-8 h-6"
                        size="size-6"
                        type="button"
                        :aria-label="__('sort-question-up', { question: question.title })"
                        :disabled="questionIndex === 0"
                        @click="
                            rememberFocus();
                            sortQuestion('up');
                            restoreFocus();
                        "
                    >
                        <ChevronUpIcon class="size-5" />
                    </IconButton>
                    <IconButton
                        class="text-gray-dark bg-gray-lightest hover:bg-gray-light"
                        area-size="w-8 h-6"
                        size="size-6"
                        type="button"
                        :aria-label="__('sort-question-down', { question: question.title })"
                        :disabled="isLastQuestion(sectionIndex, questionIndex)"
                        @click="
                            rememberFocus();
                            sortQuestion('down');
                            restoreFocus();
                        "
                    >
                        <ChevronDownIcon class="size-5" />
                    </IconButton>
                </template>
            </div>
        </div>

        <div v-if="!sortForm" class="mt-4 max-w-165">
            <InputLabel :for="`question-title-${question.id}`">
                {{ __('question-title') }}
            </InputLabel>
            <TextInput
                :id="`question-title-${question.id}`"
                v-model="question.title"
                :width="TextInputWidth.Full"
            />

            <InputLabel :for="`question-description-${question.id}`" class="mt-5">
                {{ __('question-description') }}
            </InputLabel>
            <MarkdownTextarea
                :id="`question-description-${question.id}`"
                v-model="question.description"
                :rows="2"
            />

            <template v-if="useAi">
                <InputLabel :for="`question-prompt-${question.id}`" class="mt-5">
                    <span class="flex items-center gap-1">
                        <AstroidIcon class="size-4 text-primary-beet" />
                        {{ __('question-prompt') }}
                    </span>
                </InputLabel>
                <MarkdownTextarea
                    :id="`question-prompt-${question.id}`"
                    v-model="question.prompt"
                    :rows="2"
                    :validation-key="`sections.${sectionIndex}.questions.${questionIndex}.prompt`"
                />
            </template>

            <div class="mt-5">
                <InputLabel
                    :for="`question-answer-type-${question.id}`"
                    :help-hint="__('explain', { label: __('coaching-form-answer-type') })"
                >
                    {{ __('coaching-form-answer-type') }}
                    <template #help>
                        <p>
                            <strong>{{ __('coaching-form-answer-type-affirmative') }}</strong>
                            <br />
                            {{ __('coaching-form-answer-type-affirmative-description') }}
                            <br />
                            <strong>{{ __('coaching-form-answer-type-interval') }}</strong>
                            <br />
                            {{ __('coaching-form-answer-type-interval-description') }}
                            <br />
                            <strong>{{ __('coaching-form-answer-type-custom') }}</strong>
                            <br />
                            {{ __('coaching-form-answer-type-custom-description') }}
                            <br />
                            <strong>{{ __('not-applicable') }}</strong>
                            <br />
                            {{ __('coaching-form-answer-not-applicable-description') }}
                        </p>
                    </template>
                </InputLabel>
            </div>

            <div class="flex items-end">
                <div>
                    <SelectInput
                        :id="`question-answer-type-${question.id}`"
                        v-model="question.answerType"
                    >
                        <option :value="CoachingFormAnswerType.Affirmative">
                            {{ __('coaching-form-answer-type-affirmative') }}
                        </option>
                        <option :value="CoachingFormAnswerType.Interval">
                            {{ __('coaching-form-answer-type-interval') }}
                        </option>
                        <option :value="CoachingFormAnswerType.Custom">
                            {{ __('coaching-form-answer-type-custom') }}
                        </option>
                        <option :value="CoachingFormAnswerType.Text">
                            {{ __('coaching-form-answer-type-text') }}
                        </option>
                    </SelectInput>
                </div>

                <div v-if="question.answerType !== CoachingFormAnswerType.Text" class="mb-2 ml-4">
                    <ToggleSwitch v-model="question.notApplicable" :label="__('not-applicable')" />
                </div>
            </div>

            <component
                :is="coachingFormAnswerComponents[question.answerType]"
                :question="question"
            />

            <div
                v-if="question.answerType !== CoachingFormAnswerType.Text"
                class="mt-5 border-t border-gray-light pt-5 flex flex-col gap-2.5"
            >
                <CheckboxInput
                    :id="`question-is-critical-${question.id}`"
                    v-model="question.isCritical"
                    :help-hint="__('explain', { label: __('coaching-form-critical-question') })"
                >
                    {{ __('coaching-form-critical-question') }}

                    <template #help>
                        <p>
                            {{ __('coaching-form-critical-question-help-text') }}
                        </p>
                    </template>
                </CheckboxInput>

                <CheckboxInput
                    :id="`question-use-comments-${question.id}`"
                    v-model="question.useComments"
                >
                    {{ __('coaching-form-use-comments') }}
                </CheckboxInput>
            </div>
        </div>
    </div>
</template>
